import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Buildings, CaretRight, Plus, PencilSimple } from "@phosphor-icons/react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { toast } from "sonner";

export function healthTone(h) {
  if (h >= 80) return "text-emerald-600";
  if (h >= 50) return "text-amber-600";
  return "text-red-600";
}
export const STATUS_TONE = {
  "Betriebsbereit": "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
  "Wartung fällig": "border-amber-500/40 bg-amber-500/10 text-amber-600",
  "Mängel vorhanden": "border-amber-500/40 bg-amber-500/10 text-amber-600",
  "Sicherheitsrelevanter Mangel": "border-red-500/40 bg-red-500/10 text-red-600",
  "Außer Betrieb": "border-red-500/40 bg-red-500/10 text-red-600",
  "Modernisierung empfohlen": "border-blue-500/40 bg-blue-500/10 text-blue-600",
};

const empty = { customer_id: "", name: "", address: "", region: "", street: "", house_number: "", postal_code: "", city: "", state: "", country: "", lat: null, lon: null };

function SiteDialog({ open, onOpenChange, customers, initial, onSaved }) {
  const [form, setForm] = useState(empty);
  useEffect(() => { setForm(initial ? { ...empty, ...initial } : empty); }, [initial, open]);
  const onAddress = (a) => setForm((f) => ({
    ...f, address: a.label, street: a.street, house_number: a.house_number,
    postal_code: a.postal_code, city: a.city, state: a.state, country: a.country,
    lat: a.lat, lon: a.lon, region: a.city || a.state || f.region,
  }));
  const save = async () => {
    if (!form.customer_id || !form.name) return;
    const payload = {
      customer_id: form.customer_id, name: form.name, address: form.address || null, region: form.region || null,
      street: form.street || null, house_number: form.house_number || null, postal_code: form.postal_code || null,
      city: form.city || null, state: form.state || null, country: form.country || null,
      lat: form.lat ?? null, lon: form.lon ?? null,
    };
    try {
      if (initial?.id) await api.put(`/sites/${initial.id}`, payload);
      else await api.post("/sites", payload);
      toast.success(initial?.id ? "Standort aktualisiert" : "Standort angelegt");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="site-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Standort bearbeiten" : "Neuer Standort"}</DialogTitle></DialogHeader>
        <DialogDescription>Ein Standort gehört zu einem Kunden und bündelt dessen Anlagen.</DialogDescription>
        <div className="space-y-3">
          <div><Label2>Kunde</Label2>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger data-testid="site-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label2>Name</Label2><Input data-testid="site-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Verladehof Nord" /></div>
          <div><Label2>Adresse (Suche)</Label2>
            <AddressAutocomplete testid="site-address" value={form.address}
              onChange={(t) => setForm((f) => ({ ...f, address: t }))} onSelect={onAddress} />
          </div>
          {(form.postal_code || form.city || form.street) && (
            <div className="grid grid-cols-2 gap-2 rounded-md bg-muted/40 p-2 text-xs" data-testid="site-address-fields">
              <div><Label2>Straße/Nr.</Label2><div>{[form.street, form.house_number].filter(Boolean).join(" ") || "—"}</div></div>
              <div><Label2>PLZ / Ort</Label2><div>{[form.postal_code, form.city].filter(Boolean).join(" ") || "—"}</div></div>
              <div><Label2>Bundesland</Label2><div>{form.state || "—"}</div></div>
              <div><Label2>Land · Koord.</Label2><div>{form.country || "—"}{form.lat ? ` · ${Number(form.lat).toFixed(4)}, ${Number(form.lon).toFixed(4)}` : ""}</div></div>
            </div>
          )}
          <div><Label2>Region (Kürzel, optional)</Label2><Input data-testid="site-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="z. B. Heilbronn" /></div>
        </div>
        <DialogFooter><Button data-testid="site-save" onClick={save} disabled={!form.customer_id || !form.name}>Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Standorte() {
  const [sites, setSites] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/sites").then((r) => setSites(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!sites) return <div className="grid gap-4 sm:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 rounded-md" />)}</div>;

  const groups = sites.reduce((m, s) => { const k = s.customer_name || "Ohne Kunde"; (m[k] = m[k] || []).push(s); return m; }, {});

  return (
    <div>
      <PageHeader title="Standorte" subtitle="Kunde → Standort → Anlage · Wartung, Status & Gesundheit je Standort">
        <Button size="sm" className="gap-2" data-testid="new-site-btn" onClick={() => setDlg({ open: true, initial: null })}><Plus size={15} weight="bold" /> Neuer Standort</Button>
      </PageHeader>
      {sites.length === 0 && <Card className="rounded-md border p-8 text-center text-sm text-muted-foreground">Noch keine Standorte. Lege den ersten über „Neuer Standort" an.</Card>}
      {Object.entries(groups).map(([cust, list]) => (
        <div key={cust} className="mb-6">
          <div className="mb-2 flex items-center gap-2"><Buildings size={16} weight="duotone" className="text-primary" /><Label2>{cust}</Label2><span className="text-xs text-muted-foreground">({list.length})</span></div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((s) => (
              <Card key={s.id} data-testid={`site-card-${s.id}`}
                className="group relative rounded-md border p-5 transition-all hover:-translate-y-[1px] hover:border-primary/40 hover:shadow-sm">
                <button onClick={() => navigate(`/standorte/${s.id}`)} className="absolute inset-0" aria-label={`Standort ${s.name} öffnen`} />
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin size={22} weight="duotone" className="text-primary" />
                    <div>
                      <div className="font-heading text-base font-bold tracking-tight">{s.name}</div>
                      {s.address && <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={12} /> {s.address}</div>}
                    </div>
                  </div>
                  <CaretRight size={18} className="text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  {s.region ? <Badge variant="outline" className="rounded-md">{s.region}</Badge> : <span />}
                  <Button size="icon" variant="ghost" className="relative z-10 h-7 w-7" data-testid={`site-edit-${s.id}`}
                    onClick={(e) => { e.stopPropagation(); setDlg({ open: true, initial: s }); }}><PencilSimple size={14} /></Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
      <SiteDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} customers={customers} initial={dlg.initial} onSaved={load} />
    </div>
  );
}
